import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, describeApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Replace, Lock, CheckCircle2, AlertTriangle } from "lucide-react";

const usd = (v) => (v == null ? "—" : `$${Number(v).toFixed(4)}`);
const HIDDEN_STATES = ["NO_RUN", "NOT_APPLICABLE"];

// P6N — sostituzione ESPLICITA Owner di S6 (HARD3 Fast) con S6R1: un solo
// GET finale sulla vecchia response, poi al massimo UNA nuova POST Fast solo
// se ancora in coda. Un tap = una sola richiesta (lock useRef + DOM).
export const HardenedReplacementPanel = ({ jobId }) => {
  const [r, setR] = useState(null);
  const [checked, setChecked] = useState(false);
  const [phrase, setPhrase] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [accepted, setAccepted] = useState(null);
  const [err, setErr] = useState(null);
  const [attempted, setAttempted] = useState(false);
  const lock = useRef(false);
  const btnRef = useRef(null);
  const cbRef = useRef(null);
  const inpRef = useRef(null);

  const load = useCallback(() => {
    if (!jobId) return;
    api.get(`/reader/jobs/${jobId}/segmented-run-replacement/status`)
      .then(({ data }) => setR(data))
      .catch((e) => setR({ state: "LOAD_ERROR", load_error: describeApiError(e) }));
  }, [jobId]);

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  if (!r) return null;
  if (r.load_error) {
    return (
      <div className="text-[11px] border border-red-300 bg-red-50 p-2 text-red-900" data-testid="replacement-load-error">
        Stato sostituzione non disponibile: {r.load_error.message}
      </div>
    );
  }
  if (HIDDEN_STATES.includes(r.state)) return null;

  const o = r.original || {};
  const a = r.attempt;
  const b = r.budget || {};
  const ra = r.replacement_authorization;
  const sid = r.segment_id || "S6";
  const disp = r.attempt_display_id || "S6R1";
  const PHRASE = ra?.confirmation_phrase || r.confirmation_phrase || "";
  const phraseOk = PHRASE.length > 0 && phrase === PHRASE;
  const canStart = !!r.startable && checked && phraseOk && !submitting && !accepted && !attempted;
  const locked = submitting || !!accepted || attempted || !r.startable;
  const inProgress = r.state === "REPLACEMENT_IN_PROGRESS";
  const merged = r.state === "ATTEMPT_COMPLETED_RUN_MERGED_HUMAN_REVIEW_REQUIRED";
  const origDone = r.state === "ORIGINAL_COMPLETED_AT_FINAL_GET_NO_NEW_POST";
  const showPre = r.preconditions && !r.preconditions.ok && !inProgress && !a && !merged && !origDone && !(ra && ra.consumed);

  const onExecute = async (ev) => {
    ev?.preventDefault?.();
    if (lock.current) return;
    lock.current = true;
    [btnRef.current, cbRef.current, inpRef.current].forEach((el) => { if (el) el.disabled = true; });
    setSubmitting(true); setAttempted(true); setErr(null);
    try {
      const res = await api.post(`/reader/jobs/${jobId}/segmented-run-replacement/execute`,
        { confirm: true, confirmation_text: phrase });
      if (res.status === 202 || res.status === 200) { setAccepted(res.data || { accepted: true }); load(); }
      else setErr({ status: res.status, code: null, message: `Risposta inattesa HTTP ${res.status}.` });
    } catch (e) {
      setErr(describeApiError(e)); load();
    } finally { setSubmitting(false); }
  };

  return (
    <div className="w-full min-w-0 border-2 border-sky-400 bg-sky-50/40 p-2 sm:p-3 space-y-2 break-anywhere"
         data-testid="replacement-panel">
      <div className="font-bold text-sm flex items-center gap-2" data-testid="replacement-title">
        <Replace size={15} className="text-rose-700 shrink-0" />
        {r.panel_title || `Sostituzione esplicita ${sid} → ${disp}`} — decisione Owner, non un retry
      </div>
      <div className="text-[11px]" data-testid="replacement-explanation">{r.explanation}</div>
      <div className="text-[11px] px-2 py-1 border border-rose-300 bg-white" data-testid="replacement-state">
        Stato sostituzione: <b>{r.state}</b>
        {merged && <span> — {disp} completato: fusione S0–S5 + {disp} eseguita, esito <b>{r.final_state_forced}</b>.</span>}
        {origDone && <span> — la response originale è risultata completata al GET finale: nessuna nuova POST.</span>}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-[11px]" data-testid="replacement-facts">
        <div>{sid} originale: <b data-testid="replacement-original-state">{o.state || "—"}</b>{o.last_provider_status && <span> · provider <b data-testid="replacement-provider-status">{o.last_provider_status}</b></span>}{r.original_superseded && <span className="text-rose-800"> · superata (conservata)</span>}</div>
        <div>response_id: <span className="font-mono" data-testid="replacement-response-id">{o.response_id_short || "—"}</span>{o.response_id_present && <span className="text-emerald-800" data-testid="replacement-response-id-kept"> (conservata)</span>}</div>
        <div>POST {sid} = <b data-testid="replacement-post-count">{o.post_count ?? "—"}</b> · GET già effettuati: <b data-testid="replacement-get-count">{o.get_count ?? "—"}</b> · GET finale previsto: <b>{r.limits?.final_get_on_original ?? 1}</b></div>
        <div>Costo confermato attuale: <b data-testid="replacement-cost-confirmed">{usd(b.confirmed_usd)}</b></div>
        <div>Esposizione incerta originale ({sid}): <b data-testid="replacement-original-exposure">{usd(b.original_uncertain_exposure_usd)}</b></div>
        <div>Nuova esposizione massima {disp}: <b data-testid="replacement-attempt-exposure">{usd(b.attempt_worst_case_usd)}</b></div>
        <div>Esposizione totale massima: <b data-testid="replacement-total-exposure">{usd(b.total_max_exposure_usd)}</b> su tetto <b data-testid="replacement-cap">{usd(b.cap_usd)}</b> (residuo <span data-testid="replacement-residual">{usd(b.residual_after_max_exposure_usd)}</span>)</div>
        <div>Nuove POST Fast possibili: <b data-testid="replacement-max-posts">{r.limits?.max_new_posts ?? 1}</b> — esclusivamente per {disp} · retry: <b>nessuno</b></div>
        <div>Polling {disp}: max {r.limits?.attempt_max_gets} GET / {Math.round((r.limits?.attempt_max_seconds || 0) / 60)} min · backoff {(r.limits?.backoff_s || []).join(" / ")} s</div>
        <div>Esito finale obbligatorio: <b data-testid="replacement-final-state">{r.final_state_forced || "HUMAN_REVIEW_REQUIRED"}</b></div>
        <div className="sm:col-span-2" data-testid="replacement-frozen-segments">
          S0–S5 già completati e non modificati: {(r.frozen_segments || []).map((s) => (
            <span key={s.segment_id} className="font-mono mr-1.5" data-testid={`replacement-frozen-${s.segment_id}`}>{s.segment_id}✓</span>
          ))}<span className="text-muted-foreground">(non vengono ripetuti)</span>
        </div>
      </div>
      <div className="text-[10px] border border-amber-300 bg-amber-50 p-1.5 text-amber-900" data-testid="replacement-double-exposure-note">{r.double_exposure_note}</div>

      {a && (
        <div className="text-[11px] border border-rose-300 bg-white p-2 space-y-0.5" data-testid="replacement-attempt">
          <div>{disp}: stato <b data-testid="replacement-attempt-state">{a.state}</b> · POST <b data-testid="replacement-attempt-posts">{a.post_count}</b> · GET <b>{a.get_count}</b>{a.last_provider_status && <span> · provider <b>{a.last_provider_status}</b></span>}{a.response_id_short && <span> · response_id <span className="font-mono">{a.response_id_short}</span></span>}</div>
          {r.attempt_details && <div className="text-[10px] text-muted-foreground">attempt_id <span className="font-mono">{r.attempt_details.attempt_id}</span> · stesso input canonico dell'originale: <b>{r.attempt_details.same_canonical_input_as_original ? "sì" : "NO"}</b>{r.attempt_details.real_cost_usd != null && <span> · costo {usd(r.attempt_details.real_cost_usd)}</span>}</div>}
          {a.errors?.length > 0 && <div className="text-[10px] text-red-900" data-testid="replacement-attempt-error">Errore provider reale: {a.errors.map((e) => [e.phase, e.envelope_status, e.exception_type, e.detail].filter(Boolean).join(" · ")).join("; ")}</div>}
        </div>
      )}
      {(r.events || []).length > 0 && (
        <div className="text-[10px] text-muted-foreground space-y-0.5" data-testid="replacement-events">
          {r.events.map((e, i) => <div key={i}>Evento append-only: <b>{e.decision}</b> {(e.created_at || "").slice(0, 16)}{e.final_get_status ? ` · GET finale: ${e.final_get_status}` : ""}</div>)}
        </div>
      )}
      {r.late_original_result && (
        <div className="text-[11px] border border-orange-400 bg-orange-50 p-2 text-orange-900" data-testid="replacement-late-result">
          La response originale è risultata completata dopo la sostituzione: risultato conservato come <b>LATE_SUPERSEDED_RESULT</b>, {disp} non sovrascritto, conflitto in revisione umana{r.late_original_result.outputs_differ ? " (output differenti)" : " (output identici)"}.
        </div>
      )}

      {ra && (
        <div className="text-[10px] text-muted-foreground" data-testid="replacement-auth">
          Autorizzazione sostitutiva <span className="font-mono">{ra.id}</span> · {ra.segment_id} → {ra.attempt_display_id} · {ra.consumed ? `consumata ${(ra.consumed_at || "").slice(0, 16)}` : "non consumata"} · scade {(ra.expires_at || "").slice(0, 16)} · max {ra.max_new_posts ?? 1} POST
          {ra.outcome && <span> · esito: <b>{ra.outcome}</b> ({ra.posts_performed ?? 0} POST, {ra.gets_performed ?? 0} GET)</span>}
        </div>
      )}
      {!ra && r.replacement_applicable && (
        <div className="text-[11px] border border-rose-300 bg-white p-2" data-testid="replacement-no-auth">
          Autorizzazione sostitutiva non ancora predisposta: serve una decisione Owner separata.
        </div>
      )}
      {showPre && (
        <div className="text-[11px] border border-red-300 bg-red-50 p-2 text-red-900" data-testid="replacement-precondition-failed">
          Precondizioni non soddisfatte: {r.preconditions.failing.join(", ")}
          {r.original_worker_id && <span> — worker <b>{r.original_worker_id}</b> con lease fino a {(r.original_lease_expires_at || "").slice(0, 19)}</span>}
        </div>
      )}

      {accepted && (
        <div className="flex items-start gap-1.5 text-[11px] border border-emerald-400 bg-emerald-50 p-2 text-emerald-900" data-testid="replacement-accepted">
          <CheckCircle2 size={13} className="mt-0.5 shrink-0" />
          <span>Sostituzione accettata (HTTP 202): un GET finale su {sid}, poi al massimo {accepted.max_new_posts ?? 1} POST Fast per {accepted.attempt_display_id || disp}. Autorizzazione consumata.</span>
        </div>
      )}
      {err && (
        <div className="text-[11px] border border-red-400 bg-red-50 p-2 text-red-900 space-y-0.5" data-testid="replacement-error">
          <div className="font-bold">Sostituzione non eseguita{err.status ? ` — HTTP ${err.status}` : ""}{err.code ? ` · ${err.code}` : ""}</div>
          <div>{err.message}</div>
          <div className="text-[10px]">Nessun nuovo tentativo automatico.</div>
        </div>
      )}

      {r.startable && !accepted && !attempted && (
        <div className="border-t border-rose-300 pt-2 space-y-2 safe-pb">
          <label className="flex items-start gap-2 text-[11px]">
            <input type="checkbox" ref={cbRef} checked={checked} disabled={locked}
                   onChange={(e) => setChecked(e.target.checked)} className="mt-0.5 w-5 h-5 shrink-0"
                   data-testid="replacement-owner-checkbox" />
            <span>Confermo come Owner la sostituzione esplicita di {sid}: un solo GET finale sulla response_id salvata e, solo se ancora in coda, UNA nuova POST Fast per {disp} con lo stesso input canonico (esposizione totale massima {usd(b.total_max_exposure_usd)} su {usd(b.cap_usd)}, S0–S5 non ripetuti, esito {r.final_state_forced}).</span>
          </label>
          <input type="text" ref={inpRef} value={phrase} disabled={locked}
                 onChange={(e) => setPhrase(e.target.value)}
                 placeholder={`Digita esattamente: ${PHRASE}`}
                 className="w-full min-h-[44px] border border-border px-2 py-2 text-base font-mono bg-white"
                 data-testid="replacement-confirmation-input" />
          <Button ref={btnRef} onClick={onExecute} disabled={!canStart}
                  data-testid="replacement-execute-button"
                  className="w-full min-h-[44px] bg-rose-700 hover:bg-rose-800">
            {canStart ? <Replace size={14} className="mr-1" /> : <Lock size={14} className="mr-1" />}
            {submitting ? "Sostituzione in corso…" : `Sostituisci solo ${sid} con ${disp} (max 1 nuova POST)`}
          </Button>
        </div>
      )}
      {ra && ra.consumed && !accepted && !inProgress && !a && !merged && !origDone && (
        <div className="flex items-start gap-1.5 text-[10px] text-rose-800" data-testid="replacement-consumed">
          <AlertTriangle size={11} className="mt-0.5 shrink-0" />
          <span>Autorizzazione sostitutiva consumata (one-shot): stato storico{ra.error_detail ? ` — ${ra.error_detail}` : ""}.</span>
        </div>
      )}
    </div>
  );
};
